import { inject, Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, filter, Observable, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { getUserIdentity, getBusinessUnit } from '../auth/auth-identity';
import { WsConnectionEvent, WsConnectionState } from '../services/ws-connection.service';
import {
  RealtimeChannel,
  RealtimeEvent,
  RealtimeInboundFrame,
  RealtimeOutboundFrame,
} from './realtime.model';
import { RealtimeEventName } from './realtime-events';

/** Path the backend mounts the WS gateway at (`config.PATH`). */
const REALTIME_PATH = '/api/v2/realtime';
/** App-level keep-alive ping cadence (server heartbeat is 25s). */
const HEARTBEAT_MS = 25_000;
/** Reconnect backoff ceiling. */
const MAX_RECONNECT_DELAY_MS = 30_000;

/**
 * Native-WebSocket client for the backend realtime gateway (replaces Apollo
 * GraphQL subscriptions). One shared socket per app session.
 *
 * Responsibilities: connection lifecycle, exponential-backoff auto-reconnect,
 * heartbeat, explicit channel (un)subscription with re-subscribe on reconnect,
 * and a typed per-event stream via {@link on}.
 *
 * Usage:
 * ```ts
 * private readonly realtime = inject(RealtimeService);
 * this.realtime.on(RealtimeEvents.TASK_UPDATED)
 *   .pipe(takeUntilDestroyed())
 *   .subscribe(() => this.reload());
 * ```
 * Connection itself is owned by the app shell (admin-layout) — features only
 * listen; they never connect/disconnect.
 */
@Injectable({ providedIn: 'root' })
export class RealtimeService {
  private readonly zone = inject(NgZone);

  private socket: WebSocket | null = null;
  private readonly events$ = new Subject<RealtimeEvent>();

  /**
   * Connection state of THIS realtime socket. Kept separate from the Apollo
   * GraphQL-WS `WsConnectionService` so the two transports never clobber each
   * other's status during the dual-run migration.
   */
  readonly state$ = new BehaviorSubject<WsConnectionEvent>({
    state: WsConnectionState.CLOSED,
    reConnection: false,
  });

  /** Channels the principal is auto-joined to (from the `connected` frame). */
  private defaultChannels: RealtimeChannel[] = [];
  /** Channels explicitly requested by features — re-sent after every reconnect. */
  private readonly desiredChannels = new Set<RealtimeChannel>();

  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private manualClose = false;
  private hasConnectedBefore = false;

  // ── Public API ────────────────────────────────────────────────────────────

  /** Open the socket (idempotent). Safe to call again after a manual disconnect. */
  connect(): void {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }
    this.manualClose = false;
    this.openSocket();
  }

  /** Close the socket and stop reconnecting (call on logout / shell teardown). */
  disconnect(): void {
    this.manualClose = true;
    this.clearTimers();
    if (this.socket) {
      try { this.socket.close(); } catch { /* noop */ }
      this.socket = null;
    }
    this.defaultChannels = [];
    this.reconnectAttempts = 0;
    this.setState(WsConnectionState.CLOSED, false);
  }

  /** Stream of a single event (across whatever channels deliver it). */
  on<T = unknown>(event: RealtimeEventName | string): Observable<RealtimeEvent<T>> {
    return this.events$.pipe(
      filter((e): e is RealtimeEvent<T> => e.event === event),
    );
  }

  /** Stream of several events merged (e.g. "any task change → reload"). */
  onAny<T = unknown>(events: readonly (RealtimeEventName | string)[]): Observable<RealtimeEvent<T>> {
    const set = new Set<string>(events);
    return this.events$.pipe(
      filter((e): e is RealtimeEvent<T> => set.has(e.event)),
    );
  }

  /**
   * Subscribe to a non-default channel (e.g. `device:<id>` or `task:<id>`).
   * Tracked so it is re-sent automatically after a reconnect.
   */
  subscribeChannel(channel: RealtimeChannel): void {
    this.desiredChannels.add(channel);
    this.send({ type: 'subscribe', channel });
  }

  unsubscribeChannel(channel: RealtimeChannel): void {
    this.desiredChannels.delete(channel);
    this.send({ type: 'unsubscribe', channel });
  }

  // ── Connection internals ────────────────────────────────────────────────────

  private openSocket(): void {
    const isReconnect = this.hasConnectedBefore;
    this.setState(
      isReconnect ? WsConnectionState.RECONNECTING : WsConnectionState.CONNECTING,
      isReconnect,
    );

    let ws: WebSocket;
    try {
      ws = new WebSocket(this.buildUrl());
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket = ws;

    ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.hasConnectedBefore = true;
      this.setState(WsConnectionState.CONNECTED, isReconnect);
      // Re-assert explicit subscriptions (defaults are auto-joined server-side).
      for (const channel of this.desiredChannels) this.send({ type: 'subscribe', channel });
      this.startHeartbeat();
    };

    ws.onmessage = (ev) => this.handleFrame(ev.data);

    ws.onerror = () => {
      this.setState(WsConnectionState.ERROR, false);
    };

    ws.onclose = () => {
      this.stopHeartbeat();
      this.socket = null;
      if (this.manualClose) return;
      this.setState(WsConnectionState.CLOSED, false);
      this.scheduleReconnect();
    };
  }

  private buildUrl(): string {
    const base = environment.realtimeWsUri.replace(/\/+$/, '');
    const userdetail = encodeURIComponent(JSON.stringify(getUserIdentity()));
    const businessUnit = encodeURIComponent(getBusinessUnit());
    return `${base}${REALTIME_PATH}?userdetail=${userdetail}&business_unit=${businessUnit}`;
  }

  private handleFrame(raw: unknown): void {
    let frame: RealtimeInboundFrame;
    try {
      frame = JSON.parse(typeof raw === 'string' ? raw : String(raw)) as RealtimeInboundFrame;
    } catch {
      return;
    }

    switch (frame.type) {
      case 'connected':
        this.defaultChannels = frame.channels ?? [];
        break;
      case 'event':
        // Deliver inside the Angular zone so OnPush views update even if the
        // app later goes zoneless.
        this.zone.run(() => this.events$.next({
          channel: frame.channel,
          event: frame.event,
          data: frame.data,
          id: frame.id,
        }));
        break;
      case 'subscribed':
      case 'unsubscribed':
      case 'pong':
      case 'error':
      default:
        break;
    }
  }

  private send(frame: RealtimeOutboundFrame): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      try { this.socket.send(JSON.stringify(frame)); } catch { /* noop */ }
    }
  }

  private scheduleReconnect(): void {
    if (this.manualClose || this.reconnectTimer) return;
    const delay = Math.min(MAX_RECONNECT_DELAY_MS, 1000 * 2 ** this.reconnectAttempts);
    const jitter = delay * 0.2 * Math.random();
    this.reconnectAttempts++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.manualClose) this.openSocket();
    }, delay + jitter);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => this.send({ type: 'ping' }), HEARTBEAT_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) { clearInterval(this.heartbeatTimer); this.heartbeatTimer = null; }
  }

  private clearTimers(): void {
    this.stopHeartbeat();
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
  }

  private setState(state: WsConnectionState, reConnection: boolean): void {
    this.zone.run(() => this.state$.next({ state, reConnection }));
  }
}
